import { EmptyElementOptions, IElement, IPage } from '../types'

import { flattenDeep } from 'lodash'
import { ELEMENTS_LOOKUP } from '../const'

export const floatingElementsSet = new Set([
  ELEMENTS_LOOKUP.INITIALS_FIELD,
  ELEMENTS_LOOKUP.SIGNATURE,
  ELEMENTS_LOOKUP.DATE_FIELD,
  ELEMENTS_LOOKUP.CHECKBOX,
  ELEMENTS_LOOKUP.TEXT_FIELD,
  ELEMENTS_LOOKUP.SAVE_CARD_DETAILS,
])

export const blockElementsSet = new Set([
  ELEMENTS_LOOKUP.TEXT,
  ELEMENTS_LOOKUP.IMAGE,
  ELEMENTS_LOOKUP.VIDEO,
  ELEMENTS_LOOKUP.TABLE,
  ELEMENTS_LOOKUP.PRODUCT_LIST,
])

/**
 * Returns a flat array of all children elements across all pages
 * From each page it considers elements present in
 * 1. page.children
 * 2. page.component.options.headers.children
 * 3. page.component.options.footers.children
 * @param pages
 * @returns
 */
export const flatChildrens = (pages: IPage[]): IElement[] => {
  /**
   * Recursively collects every descendant of `element` (depth-first, excludes `element` itself).
   * Used so fillables nested at ANY depth under a block / column cell are included in the flat
   * list — previously the ROW branch only returned the cells themselves (one level), so fields
   * sitting inside a TEXT block inside a split-column column (content-area case) were silently
   * dropped from counts, `flatFillableFields`, assignment maps, and PDF-ready gating.
   */
  const collectDescendants = (element: IElement | undefined | null): IElement[] => {
    if (!element) return []
    const descendants: IElement[] = []
    ;(element.children || []).forEach(child => {
      if (child == null) return
      descendants.push(child)
      descendants.push(...collectDescendants(child))
    })
    return descendants
  }

  const extractChildren = (element: IElement | undefined | null): IElement[] => {
    if (!element) return []
    if (element.type === ELEMENTS_LOOKUP.ROW) {
      // ROW → columns are layout containers, NOT content. Skip columns and return
      // each cell (column's direct child) plus every descendant of that cell so
      // nested fillables (e.g. COLUMN → TEXT → TextField) are surfaced.
      const result: IElement[] = []
      ;(element.children || []).forEach(column => {
        ;(column?.children || []).forEach(cell => {
          if (cell == null) return
          result.push(cell)
          result.push(...collectDescendants(cell))
        })
      })
      return result
    }
    return (element.children || []).filter((c): c is IElement => c != null)
  }

  const extractSectionChildren = (section: IElement<EmptyElementOptions> | undefined): IElement[] => {
    if (!section) return []
    const result: IElement[] = [section]
    const direct = (section.children || []).filter((c): c is IElement => c != null)
    result.push(...direct)
    result.push(...flattenDeep(direct.map(child => extractChildren(child))))
    return result
  }

  const flat = flattenDeep(
    pages.flatMap(page => [
      ...(page.children?.flatMap(element => {
        if (!element) return [] as IElement[]
        return [element, ...extractChildren(element)]
      }) || []),
      ...extractSectionChildren(page.component.options.headers),
      ...extractSectionChildren(page.component.options.footers),
    ])
  )

  return (flat as unknown[]).filter((el): el is IElement => el != null && typeof el === 'object' && 'type' in (el as object))
}

const pushBlockElementsFromTopLevel = (children: IElement[] | undefined, acc: IElement[]) => {
  children?.forEach(element => {
    if (element.type === ELEMENTS_LOOKUP.ROW) {
      element.children?.forEach(column => {
        column.children?.forEach(child => {
          if (blockElementsSet.has(child.type)) {
            acc.push(child)
          }
          if (child?.children && child?.children?.length > 0) {
            child.children.forEach(grandChild => {
              if (blockElementsSet.has(grandChild.type)) {
                acc.push(grandChild)
              }
            })
          }
        })
      })
    } else {
      if (blockElementsSet.has(element.type)) {
        acc.push(element)
      }
      if (element?.children && element?.children?.length > 0) {
        element.children.forEach(child => {
          if (blockElementsSet.has(child.type)) {
            acc.push(child)
          }
        })
      }
    }
  })
}

/**
 * Returns all block elements in the document (excluding nested block elements) which are one of
 * TEXT, IMAGE, VIDEO, TABLE, PRODUCT_LIST
 * Includes the same shapes under page headers and footers as under page.children.
 * @param pages
 * @returns
 */
export const flatBlockChildrens = (pages: IPage[]) => {
  return pages.reduce((acc, page) => {
    pushBlockElementsFromTopLevel(page.children, acc)
    pushBlockElementsFromTopLevel(page.component?.options?.headers?.children, acc)
    pushBlockElementsFromTopLevel(page.component?.options?.footers?.children, acc)
    return acc
  }, [] as IElement[])
}
